import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/storage/store.ts'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { AuthLayout } from '../layout.tsx'
import AceScholarshipsLogo from '@assets/AceScholarshipLogo1.jpg'
import { Input } from '@/app/common/components/input/Input.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import {
  bootstrapFromKeycloak,
  fetchUserPrivileges,
  removeError,
  requestLogin,
  setVerified,
} from '@/app-store/user/authSlice'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { EyeIcon, EyeSlashIcon } from '@/common/icons/general.tsx'
import AppDialog from '@/common/components/Dialog.tsx'
import { useUserPrivileges } from '../../../../hook/useUserPrivileges.tsx'
import { resetAuthState } from '../../../../storage/user/authSlice'
import { AUTH_MODE, keycloakAuthService } from '@/auth/keycloak'

const defaultRoutes = [
  { permission: 'dashboard_read', path: AppRoutePaths.dashboard.index },
  {
    permission: 'applicants_read',
    path: AppRoutePaths.applicants.pre_selected,
  },
  { permission: 'appeals_read', path: AppRoutePaths.appeals.index },
  { permission: 'task_list_view_read', path: AppRoutePaths.tasks.index },
  {
    permission: 'scholarship_management_read',
    path: AppRoutePaths.scholarships.active_scholars.index,
  },
  { permission: 'programs_read', path: AppRoutePaths.setup.program.index },
  { permission: 'users_read', path: AppRoutePaths.setup.users.index },
  { permission: 'roles_read', path: AppRoutePaths.setup.roles.index },
  { permission: 'not_access', path: AppRoutePaths.not_access.index },
]

export default function LoginPage() {
  const dispatch = useDispatch<AppDispatch>()
  const { loading } = useSelector((s: RootState) => s.auth)
  const navigate = useNavigate()

  const didCheckKeycloak = useRef(false)
  useEffect(() => {
    if (AUTH_MODE !== 'keycloak' || didCheckKeycloak.current) return

    didCheckKeycloak.current = true
    if (keycloakAuthService().isAuthenticated()) {
      NavigateNext()
    } else {
      keycloakAuthService().login()
    }
  }, [])

  const NavigateNext = async () => {
    await dispatch(bootstrapFromKeycloak())
    await dispatch(fetchUserPrivileges())
    dispatch(setVerified())
    navigate(AppRoutePaths.dashboard.index)
  }

  if (AUTH_MODE === 'keycloak') {
    return (
      <AuthLayout>
        <AuthLayout.Content className="space-y-14">
          <div className="text-center text-sm text-gray-500 py-12">
            {loading ? 'Signing you in…' : 'Redirecting to sign in…'}
          </div>
        </AuthLayout.Content>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-14">
        <LoginForm />
      </AuthLayout.Content>
    </AuthLayout>
  )
}

const UserLoginFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters long' }),
  remember_me: z.boolean(),
})
type UserLoginForm = z.infer<typeof UserLoginFormSchema>

const LoginForm = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const {
    loading,
    error,
    is2FAPending,
    isAuthenticated,
    privilegesStatus,
    authStatus,
  } = useSelector((state: RootState) => state.auth)
  const location = useLocation()

  const { fireAlert } = useAppAlert()
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<UserLoginForm>({
    resolver: zodResolver(UserLoginFormSchema),
  })

  const privileges = useUserPrivileges()

  useEffect(() => {
    const savedEmail = localStorage.getItem('savedEmail')
    const savedPassword = localStorage.getItem('savedPassword')
    if (savedEmail && savedPassword) {
      setValue('email', savedEmail)
      setValue('password', savedPassword)
      setValue('remember_me', true)
    }
  }, [setValue])

  // useEffect(() => {
  //   if (loading === false && isAuthenticated === true) {
  //     dispatch(fetchUserPrivileges())
  //     sessionStorage.setItem('sidebarOpen', "false");
  //     navigate(AppRoutePaths.programs.index)
  //   }else if (loading == false && is2FAPending == true){
  //     navigate(AppRoutePaths.auth.twofa)
  //   }
  // }, [loading, dispatch])

  useEffect(() => {
    if (loading === false && isAuthenticated === true) {
      if (privilegesStatus === 'succeeded') {
        const nextRoute = defaultRoutes.find(
          (route) =>
            privileges.user &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (privileges.user as any)[route.permission] === true
        ) ?? { permission: 'not_access', path: AppRoutePaths.not_access.index }

        const path =
          typeof nextRoute.path === 'string'
            ? nextRoute.path
            : nextRoute.path.index
        navigate(path)
      } else if (privilegesStatus === 'idle') {
        // Dispatch fetch action if privileges are not yet loaded
        dispatch(fetchUserPrivileges())
      }
    } else if (
      loading === false &&
      is2FAPending === true &&
      authStatus === 'succeeded'
    ) {
      navigate(AppRoutePaths.auth.twofa)
      dispatch(resetAuthState())
    }
  }, [
    loading,
    isAuthenticated,
    privilegesStatus,
    dispatch,
    navigate,
    privileges.user,
  ])

  useEffect(() => {
    if (error) {
      fireAlert({
        variant: 'error',
        message: error,
      }).then()
      dispatch(removeError())
      dispatch(resetAuthState())
    }
  }, [error])

  const onSubmit = async (userForm: UserLoginForm) => {
    await dispatch(
      requestLogin({
        email: userForm.email,
        password: userForm.password,
        remember_me: userForm.remember_me,
      })
    )
  }

  if (location.state?.signup_success) {
    return <EmailConfirmedView />
  }

  return (
    <>
      <div>
        <img
          alt="Ace Logo"
          src={AceScholarshipsLogo}
          className="mx-auto h-auto"
          width="100%"
        />
        <p
          data-testid="admin-login-heading"
          className="mt-3 text-center text-sm text-gray-500"
        >
          Admin Log In
        </p>
      </div>
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            Email
          </label>
          <div className="mt-1">
            <Input
              {...register('email')}
              type="email"
              required
              autoComplete="email"
              placeholder="user@gmail.com"
            />
            <FormErrorMessage message={errors.email?.message} />
          </div>
        </div>

        <div className="pb-8">
          <label
            htmlFor="password"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            Password
          </label>
          <div className="mt-1 relative flex">
            <Input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              placeholder="Enter Your Password"
            />
            <div
              className="absolute right-0 flex items-center h-full mr-2"
              onClick={() => {
                setShowPassword(!showPassword)
              }}
            >
              {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
            </div>
          </div>
          <FormErrorMessage message={errors.password?.message} />
        </div>

        <div className="pb-4 flex items-center justify-between">
          <div className="flex items-center">
            <input
              {...register('remember_me')}
              id="remember_me"
              name="remember_me"
              type="checkbox"
              onChange={() => setRememberMe(!rememberMe)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
            />
            <label
              htmlFor="remember_me"
              className="ml-3 block text-sm leading-6 text-gray-900"
            >
              Remember me
            </label>
          </div>

          <div className="text-sm leading-6">
            <Link
              to={AppRoutePaths.auth.forgot_password.enter_email}
              className="font-semibold text-primary-600 hover:text-primary-500"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <div className="flex">
          <Button type="submit" className="grow">
            {loading ? 'Logging in...' : 'Log In'}
          </Button>
        </div>
      </form>
    </>
  )
}

const EmailConfirmedView = () => {
  const navigate = useNavigate()
  const handleSubmit = () => {
    navigate(AppRoutePaths.auth.login)
  }
  return (
    <AppDialog open={true} setOpen={() => {}}>
      <div className="flex flex-col">
        <img
          alt="Ace Logo"
          src={AceScholarshipsLogo}
          className="mx-auto h-auto"
          width="100%"
        />
        <h3 className="text-lg font-semibold text-gray-900 text-center mt-8 mb-4">
          Your email has been verified
        </h3>
        <div className="text-sm text-gray-500 font-medium text-center mb-7">
          You can now sign in to your scholarship
          <br />
          account
        </div>
        <div className="flex justify-center">
          <Button onClick={handleSubmit}>Log In</Button>
        </div>
      </div>
    </AppDialog>
  )
}

/*const TwoFaForm = () => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [verificationCode, setVerificationCode] = useState(Array(6).fill(''))
  const handleCodeChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return
    const newCode = [...verificationCode]
    newCode[index] = value
    setVerificationCode(newCode)

    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus()
    }
  }
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }
  const handleNavigation = (event: SyntheticEvent) => {
    event.preventDefault()
    dispatch(setAuthenticated(true))

    navigate(AppRoutePaths.programs.index)
  }
  return (
    <form>
      <div className="flex flex-col gap-5 items-center">
        <img alt="Phone" src={PhoneIcon} className="mx-auto h-20 mb-2" />
        <div className="text-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-5">
            Verify your phone
          </h3>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-600 mb-1.5">
              Enter the verification code we sent to
            </span>
            <a className="text-sm font-semibold text-gray-900">******7859</a>
          </div>
        </div>
        <div className="flex space-x-2">
          {verificationCode.map((num, index) => (
            <Input
              key={index}
              id={`code-${index}`}
              type="text"
              maxLength={1}
              value={num}
              onKeyDown={(e) => handleKeyDown(e, index)}
              autoComplete="off"
              ref={(el) => (inputRefs.current[index] = el)}
              onChange={(e) => handleCodeChange(e.target.value, index)}
              className="w-12 h-12 text-center text-2xl border rounded-md"
            />
          ))}
        </div>
        <div className="text-center text-sm text-gray-500 mb-2">
          Didn’t receive a code?{' '}
          <span className="text-primary-600 cursor-pointer">Resend</span>
        </div>
        <Button className="w-full grow p-2" onClick={handleNavigation}>
          Continue
        </Button>
      </div>
    </form>
  )
}*/
